import { memo, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'services/Translation'
import { statusText } from 'Constants'
import Typography from 'components/common/Typography'
import { primary, textPrimary, greySecondary } from 'styles/Colors'
import DefaultCross from 'assets/icons/crosses/Default'
import { buildStatusHistoryFromRegistration } from './statusHistoryUtils'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const Modal = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 420px;
  max-width: 90vw;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 14px;
  border-bottom: 1px solid ${greySecondary};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`

const ModalBody = styled.div`
  padding: 12px 0;
  overflow-y: auto;
  flex: 1;
`

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 22px;
  gap: 14px;
  position: relative;

  &:hover {
    background: rgba(235, 124, 12, 0.04);
  }
`

const TimelineDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${({ color }) => color || primary};
  margin-top: 3px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`

const TimelineLine = styled.div`
  position: absolute;
  left: 26px;
  top: 23px;
  bottom: -10px;
  width: 2px;
  background: ${greySecondary};
`

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
`

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EmptyState = styled.div`
  padding: 32px 22px;
  text-align: center;
`

const texts = (getText) => (key) =>
  ({
    statusHistory: getText('Status History', 'StatusHistory'),
    noHistory: getText('No status history available', 'StatusHistory'),
  }[key])

const StatusHistoryModal = ({ visible, onClose, registration }) => {
  const { t } = useTranslation(texts)
  const { t: statusT } = useTranslation(statusText)
  const { formatDate: format } = useTranslation()

  const history = buildStatusHistoryFromRegistration(registration)

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (visible) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
    return undefined
  }, [visible, handleKeyDown])

  if (!visible) return null

  return (
    <Overlay
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t('statusHistory')}
    >
      <Modal>
        <ModalHeader>
          <Typography
            text={t('statusHistory')}
            size={16}
            color={textPrimary}
            style={{ fontFamily: 'coresanscr65, sans-serif' }}
          />
          <CloseButton onClick={onClose} aria-label="Close">
            <DefaultCross style={{ width: 14, height: 'auto' }} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {history.length === 0 ? (
            <EmptyState>
              <Typography text={t('noHistory')} size={13} color={textPrimary} style={{ opacity: 0.5 }} />
            </EmptyState>
          ) : (
            history.map((item, idx) => {
              const date = new Date(item.timestamp)
              const isLast = idx === history.length - 1
              return (
                <TimelineItem key={`${item.label}-${item.timestamp}-${idx}`}>
                  <TimelineDot color={item.color} />
                  {!isLast && <TimelineLine />}
                  <ItemContent>
                    <Typography
                      text={statusT(item.label) || item.label}
                      size={13}
                      color={item.color}
                      style={{ fontFamily: 'coresanscr65, sans-serif' }}
                    />
                    <DateRow>
                      <Typography
                        text={format(date, 'dd MMMM yyyy')}
                        size={12}
                        color={textPrimary}
                        style={{ opacity: 0.7 }}
                      />
                      <Typography
                        text={format(date, 'HH:mm')}
                        size={12}
                        color={primary}
                        style={{ opacity: 0.8 }}
                      />
                    </DateRow>
                  </ItemContent>
                </TimelineItem>
              )
            })
          )}
        </ModalBody>
      </Modal>
    </Overlay>
  )
}

export default memo(StatusHistoryModal)
